import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Social Post Engagement ---

export interface SocialPostEngagement {
  likes: number;
  shares: number;
  comments: number;
  reach: number;
  impressions: number;
  clicks: number;
}

// --- Social Account Document ---

export interface SocialAccount {
  id: string;
  tenant_id: string;
  platform: string; // facebook, instagram, twitter, linkedin, wechat
  account_name: string;
  account_id: string;
  access_token: string;
  refresh_token: string;
  token_expires_at: string | null;
  status: string; // active, inactive, expired
  profile_url: string;
  avatar_url: string;
  follower_count: number;
  metadata: Record<string, unknown>;
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Social Post Document ---

export interface SocialPost {
  id: string;
  tenant_id: string;
  account_id: string;
  platform: string; // facebook, instagram, twitter, linkedin, wechat
  content: string;
  post_type: string; // text, image, video, link
  media_urls: string[];
  external_post_id: string;
  scheduled_at: string | null;
  published_at: string | null;
  status: string; // draft, scheduled, published, failed
  engagement: SocialPostEngagement;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Social Message Document ---

export interface SocialMessage {
  id: string;
  tenant_id: string;
  account_id: string;
  contact_id: string;
  platform: string; // facebook, instagram, twitter, linkedin, wechat
  direction: string; // inbound, outbound
  content: string;
  message_type: string; // text, image, video, file
  external_message_id: string;
  status: string; // read, unread
  replied_at: string | null;
  conversation_id: string;
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Create/Update DTOs ---

export interface CreateSocialAccountDto {
  platform: string;
  account_name: string;
  account_id: string;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: string;
  profile_url?: string;
  avatar_url?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateSocialAccountDto {
  account_name: string;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: string;
  profile_url?: string;
  avatar_url?: string;
  metadata?: Record<string, unknown>;
}

export interface CreateSocialPostDto {
  account_id: string;
  content: string;
  post_type: string;
  media_urls?: string[];
  scheduled_at?: string;
  tags?: string[];
}

export interface UpdateSocialPostDto {
  content: string;
  post_type: string;
  media_urls?: string[];
  scheduled_at?: string;
  tags?: string[];
}

export interface ReplyMessageDto {
  content: string;
}

// --- Filter Types ---

export interface SocialAccountFilters extends SearchParams {
  platform?: string;
  status?: string;
}

export interface SocialPostFilters extends SearchParams {
  account_id?: string;
  platform?: string;
  post_type?: string;
  status?: string;
}

export interface SocialMessageFilters extends SearchParams {
  account_id?: string;
  contact_id?: string;
  platform?: string;
  direction?: string;
  status?: string;
  conversation_id?: string;
}

// ========== Social Account API Functions ==========

export async function getSocialAccounts(params?: SocialAccountFilters) {
  return apiGet<PagedResponse<SocialAccount>>('/api/v1/social-accounts', params);
}

export async function getSocialAccount(id: string) {
  return apiGet<SocialAccount>(`/api/v1/social-accounts/${id}`);
}

export async function createSocialAccount(data: CreateSocialAccountDto) {
  return apiPost<SocialAccount>('/api/v1/social-accounts', data);
}

export async function updateSocialAccount(id: string, data: UpdateSocialAccountDto) {
  return apiPut<SocialAccount>(`/api/v1/social-accounts/${id}`, data);
}

export async function deleteSocialAccount(id: string) {
  return apiDelete<void>(`/api/v1/social-accounts/${id}`);
}

export async function connectSocialAccount(id: string) {
  return apiPost<SocialAccount>(`/api/v1/social-accounts/${id}/connect`);
}

export async function disconnectSocialAccount(id: string) {
  return apiPost<SocialAccount>(`/api/v1/social-accounts/${id}/disconnect`);
}

export async function refreshSocialAccountToken(id: string) {
  return apiPost<SocialAccount>(`/api/v1/social-accounts/${id}/refresh-token`);
}

// ========== Social Post API Functions ==========

export async function getSocialPosts(params?: SocialPostFilters) {
  return apiGet<PagedResponse<SocialPost>>('/api/v1/social-posts', params);
}

export async function getSocialPost(id: string) {
  return apiGet<SocialPost>(`/api/v1/social-posts/${id}`);
}

export async function createSocialPost(data: CreateSocialPostDto) {
  return apiPost<SocialPost>('/api/v1/social-posts', data);
}

export async function updateSocialPost(id: string, data: UpdateSocialPostDto) {
  return apiPut<SocialPost>(`/api/v1/social-posts/${id}`, data);
}

export async function deleteSocialPost(id: string) {
  return apiDelete<void>(`/api/v1/social-posts/${id}`);
}

export async function publishSocialPost(id: string) {
  return apiPost<SocialPost>(`/api/v1/social-posts/${id}/publish`);
}

export async function scheduleSocialPost(id: string, scheduled_at: string) {
  return apiPost<SocialPost>(`/api/v1/social-posts/${id}/schedule?scheduled_at=${encodeURIComponent(scheduled_at)}`);
}

export async function syncPostEngagement(id: string, engagement?: SocialPostEngagement) {
  return apiPost<SocialPost>(`/api/v1/social-posts/${id}/sync-engagement`, engagement);
}

// ========== Social Message API Functions ==========

export async function getSocialMessages(params?: SocialMessageFilters) {
  return apiGet<PagedResponse<SocialMessage>>('/api/v1/social-messages', params);
}

export async function getSocialMessage(id: string) {
  return apiGet<SocialMessage>(`/api/v1/social-messages/${id}`);
}

export async function markMessageRead(id: string) {
  return apiPost<SocialMessage>(`/api/v1/social-messages/${id}/mark-read`);
}

export async function replyToMessage(id: string, data: ReplyMessageDto) {
  return apiPost<SocialMessage>(`/api/v1/social-messages/${id}/reply`, data);
}
